import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Grid, Box, Typography } from '@mui/material';
import OverflowingTooltipText from 'components/OverflowingTooltipText';
import Button from 'components/V2/Button';
import Chip from 'components/V2/Chip';
import PropTypes from 'prop-types';
import CustomFieldFlowChart from 'views/V2/Integration/components/CustomFieldFlowChart';
import { getGuestyPassCriteria, passCriteriaOptionsInfo } from 'views/V2/Integration/constants';

import { selectIsV1IntegrationCustomer } from 'redux/selectors/integrations';
import { useGetDefaultBrandQuery } from 'redux/slices/globalApi';

import styles from './brandPreview.module.css';

import { ReactComponent as EditIcon } from 'asset/IconsPack/hx_pencil-02.svg';

import WorkflowThemePreview from '../../../../../../../components/WorkflowThemePreview';
import PassCriteriaHeader from '../PassCriteriaHeader';
import PassCriteriaPreview from '../PassCriteriaPreview';
import WorkflowPreview from '../WorkflowPreview';

function BrandPreview({ isProcessing, canUserUpdate }) {
    const history = useHistory();
    const { integrationName } = useParams();

    const { data } = useGetDefaultBrandQuery({ integrationName });

    const isV1Customer = useSelector((state) =>
        selectIsV1IntegrationCustomer(state, integrationName)
    );

    const defaultBrandId = data?.uuid;

    const handleEditBrandRedirect = async () => {
        history.push(`${history?.location?.pathname}/brands/${defaultBrandId}/edit`);
    };

    const brandPassCriteria = data?.passCriteria ?? Object.keys(passCriteriaOptionsInfo);
    const passCriteria = getGuestyPassCriteria(data?.preferredWorkflowConfig)
        ?.filter(
            (criteria) => !criteria.disabled && (brandPassCriteria || [])?.includes(criteria.value)
        )
        .map((item) => item.value);

    return (
        <Grid className={styles.previewContainer}>
            <Box className={styles.brandHeader}>
                <Box className={styles.headingBox}>
                    <Chip content="Default" size="lg" className={styles.defaultTag} />
                    <Button
                        intent="secondary"
                        startIcon={<EditIcon className={styles.editIcon} />}
                        onClick={handleEditBrandRedirect}
                        disabled={isProcessing || !canUserUpdate}
                    >
                        Edit Brand
                    </Button>
                </Box>
                <OverflowingTooltipText title={data?.brandName} className={styles.brandHeading} />
            </Box>
            {(isV1Customer === null || isV1Customer === true) && (
                <Box className={styles.previewSection}>
                    <PassCriteriaHeader
                        titleClassName={styles.previewSectionHeading}
                        workflowConfig={data?.preferredWorkflowConfig ?? {}}
                    />
                    <PassCriteriaPreview
                        data={passCriteria ?? []}
                        currentWorkflow={data?.preferredWorkflowConfig ?? {}}
                    />
                </Box>
            )}
            {isV1Customer === false && (
                <Box className={styles.previewSection}>
                    <Typography className={styles.previewSectionHeadingV2}>
                        Workflow‑Driven Custom Field Mapping
                    </Typography>
                    <CustomFieldFlowChart workflowConfig={data?.preferredWorkflowConfig ?? {}} />
                </Box>
            )}
            <Box className={styles.workflowSection}>
                <Box className={styles.previewSection}>
                    <Typography className={styles.previewSectionHeading}>Workflow</Typography>
                    <WorkflowPreview
                        className={styles.workflowPreviewBox}
                        workflowData={data?.preferredWorkflowConfig}
                        isEditBrandForm={false}
                        sevenYearCriminalCheckFallback={data?.sevenYearCriminalCheckFallback}
                    />
                </Box>
                <Box className={styles.previewSection}>
                    <Typography className={styles.previewSectionHeading}>Theme</Typography>
                    <WorkflowThemePreview
                        previewScreens={2}
                        workflowData={data?.preferredWorkflowConfig}
                    />
                </Box>
            </Box>
        </Grid>
    );
}

BrandPreview.propTypes = {
    isProcessing: PropTypes.bool,
    canUserUpdate: PropTypes.bool,
};

export default BrandPreview;
